import { Chat } from "components";
import { MainLayout } from "layouts";
import { useAccount } from "shared/hooks";

type FileList = Array<{
	name: string;
	size: number;
	type: string;
	url: string;
}>;

interface MessageContent {
	incoming?: boolean;
	outgoing?: boolean;
	avatarUrl?: string;
	senderName?: string;
	type?: "text" | "image" | "video" | "code";
	content: string;
	thinking?: boolean;
	thinkingContent?: string;
	files?: FileList;
	pending?: boolean;
	error?: boolean;
}

const messages: Array<MessageContent> = [
	{
		outgoing: true,
		senderName: "Você",
		type: "text",
		content: "Pode analisar o gráfico de performance que estou enviando e me dar o código para replicar essa estilização?",
		files: [
			{
				name: "performance_chart.png",
				size: 154200,
				type: "image/png",
				url: "blob:https://chat.demo/files/chart_01",
			},
		],
	},
	{
		incoming: true,
		senderName: "Gemini",
		avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=500&q=80",
		thinking: true,
		thinkingContent:
			"O usuário enviou um gráfico de linhas com gradiente e bordas arredondadas. Ele usa a biblioteca Recharts ou Chart.js. Vou focar em Recharts por ser mais comum em React.",
		type: "text",
		content: `Com certeza! O gráfico utiliza um gradiente linear suave e eixos customizados. Aqui está uma implementação simplificada usando React e Recharts:

\`\`\`jsx
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PerformanceChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={300}>
    <AreaChart data={data}>
      <defs>
        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
          <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey="uv" stroke="#8884d8" fillOpacity={1} fill="url(#colorUv)" />
    </AreaChart>
  </ResponsiveContainer>
);
\`\`\``,
	},
	{
		outgoing: true,
		senderName: "Usuário",
		type: "text",
		content:
			"Pode me explicar a física de um pêndulo simples e comparar com um pêndulo físico? Mande também a fórmula do período e um exemplo em Python para calcular o $T$.",
	},
	{
		incoming: true,
		senderName: "Gemini",
		avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=500&q=80",
		thinking: true,
		thinkingContent:
			"O usuário quer uma explicação acadêmica. Vou estruturar com: 1. Definição em lista, 2. Fórmulas em LaTeX, 3. Tabela comparativa e 4. Bloco de código funcional.",
		type: "text",
		content: `
### O Pêndulo Simples

Um pêndulo simples consiste em uma massa pontual $m$ suspensa por um fio ideal de comprimento $L$ e massa desprezível. 

**Principais características:**
* **Movimento Harmônico Simples (MHS):** Para pequenos ângulos ($\\theta < 10^\\circ$).
* **Força Restauradora:** $F = -mg \\sin(\\theta)$.

A fórmula para o período $T$ de um pêndulo simples é:

$$T = 2\\pi \\sqrt{\\frac{L}{g}}$$

---

### Comparativo: Simples vs. Físico

Aqui está uma tabela comparando as principais diferenças:

| Característica | Pêndulo Simples | Pêndulo Físico |
| :--- | :--- | :--- |
| **Distribuição de Massa** | Pontual (na extremidade) | Distribuída pelo corpo |
| **Braço de Alavanca** | Comprimento do fio $L$ | Distância ao centro de massa $d$ |
| **Inércia** | $I = mL^2$ | Momento de inércia $I$ do corpo |
| **Fórmula do Período** | $T = 2\\pi \\sqrt{\\frac{L}{g}}$ | $T = 2\\pi \\sqrt{\\frac{I}{mgd}}$ |

---

### Exemplo em Python

Você pode usar o código abaixo para calcular o período rapidamente:

\`\`\`python
import math

def calcular_periodo(L, g=9.81):
    """
    Calcula o período de um pêndulo simples.
    L: comprimento do fio (metros)
    g: aceleração da gravidade (m/s^2)
    """
    return 2 * math.pi * math.sqrt(L / g)

# Exemplo para um fio de 2 metros
comprimento = 2.0
periodo = calcular_periodo(comprimento)
print(f"O período é de {periodo:.2f} segundos.")
\`\`\`

> **Nota:** Lembre-se que estas fórmulas assumem um ambiente sem resistência do ar e ângulos de oscilação pequenos!`,
	},
	{
		outgoing: true,
		senderName: "Você",
		type: "text",
		content: "Ficou perfeito, era exatamente isso que eu precisava!",
		pending: true,
	},
];

export const TeamSetupPage = () => {
	const user = useAccount();

	const handleSend = async (message: string) => {
		console.log("Mensagem enviada:", message);
		await new Promise((resolve) => setTimeout(resolve, 1000 * 3));
	};

	return (
		<MainLayout contentStyle={{ overflow: "hidden" }}>
			<Chat onSend={handleSend}>
				{messages.map((msg, index) => {
					msg.avatarUrl = msg.outgoing ? (user?.profilePictureUrl ?? undefined) : msg.avatarUrl;
					return (
						<Chat.Message
							id={index.toString()}
							key={index}
							{...(msg as any)}
						>
							{msg.content}
						</Chat.Message>
					);
				})}
			</Chat>
		</MainLayout>
	);
};
